import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';

import { ArrowLeftIcon, ArrowRightIcon } from '../icons';
import withFontClassName from '../../hoc/with-font-class-name';
import withTranslate from '../../hoc/with-translate';
import styles from './pagination.scss';
import { handleEnterKeyUp } from '../../services/accessibility';

export class Pagination extends Component {
  static propTypes = {
    entityCount: PropTypes.number.isRequired,
    showPerPage: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    activeClass: PropTypes.string,
    arrowClass: PropTypes.string,
    innerClass: PropTypes.string,
    arrowLeft: PropTypes.node,
    arrowRight: PropTypes.node,
    className: PropTypes.string,
    page: PropTypes.number,
    contentFontClassName: PropTypes.string.isRequired,
    t: PropTypes.func.isRequired,
  };

  static defaultProps = {
    arrowLeft: <ArrowLeftIcon className="forum-icon-fill"/>,
    arrowRight: <ArrowRightIcon className="forum-icon-fill"/>,
  };

  constructor(props) {
    super(props);

    const { entityCount, showPerPage, page } = props;
    this.state = {
      currentPage: page || 1,
      numberCount: Math.ceil(entityCount / (showPerPage || 1)),
    };

    this.handleClick = this.handleClick.bind(this);
  }

  componentWillReceiveProps({ entityCount, showPerPage, page }) {
    if (entityCount && showPerPage) {
      const numberCount = Math.ceil(entityCount / (showPerPage || 1));
      this.setState({ numberCount });
    }
    if (page) {
      this.setState({ currentPage: page });
    }
  }

  renderNumbers() {
    const { currentPage, numberCount } = this.state;

    if (numberCount < 5) {
      return _.range(1, numberCount + 1).map(page => this.renderPage(page));
    }

    if (currentPage + 3 > numberCount) {
      return _.range(numberCount - 4, numberCount + 1).map(page => this.renderPage(page));
    }

    if (currentPage > 3) {
      return _.range(currentPage - 2, currentPage + 3).map(page => this.renderPage(page));
    }

    return _.range(1, 6).map(page => this.renderPage(page));
  }

  renderPage(page) {
    const { activeClass, innerClass, t } = this.props;
    const isActive = page === this.state.currentPage;
    const pageClassName = classNames(styles.page, innerClass, {
      [activeClass]: isActive,
      'button-color': isActive,
    });
    const handleClick = this.handleClick(page, Pagination.PAGE);
    return (
      <div
        key={page}
        className={pageClassName}
        onClick={handleClick}
        onKeyUp={handleEnterKeyUp(handleClick)}
        tabIndex={!isActive && '0'}
        aria-label={t('pagination.page', { page })}
        data-hook={`pagination__${page}`}
      >
        {page}
      </div>
    );
  }

  renderFirst() {
    const { arrowClass, arrowLeft, t } = this.props;
    const { currentPage } = this.state;
    const handleClick = this.handleClick(1, Pagination.FIRST);

    if (currentPage > 3) {
      return (
        <div
          className={classNames(styles.first, arrowClass)}
          onClick={handleClick}
          onKeyUp={handleEnterKeyUp(handleClick)}
          tabIndex="0"
          aria-label={t('pagination.first-page')}
          data-hook="pagination__first"
        >
          {arrowLeft}
          {arrowLeft}
        </div>
      );
    }
    return null;
  }

  renderPrevious() {
    const { arrowClass, arrowLeft, t } = this.props;
    const { currentPage } = this.state;
    const handleClick = this.handleClick(currentPage - 1, Pagination.PREVIOUS);

    if (currentPage > 1) {
      return (
        <div
          className={classNames(styles.previous, arrowClass)}
          onClick={handleClick}
          onKeyUp={handleEnterKeyUp(handleClick)}
          tabIndex="0"
          aria-label={t('pagination.previous-page')}
          data-hook="pagination__previous"
        >
          {arrowLeft}
        </div>
      );
    }
    return null;
  }

  renderNext() {
    const { arrowClass, arrowRight, t } = this.props;
    const { currentPage, numberCount } = this.state;
    const handleClick = this.handleClick(currentPage + 1, Pagination.NEXT);

    if (currentPage < numberCount) {
      return (
        <div
          className={classNames(styles.next, arrowClass)}
          onClick={handleClick}
          onKeyUp={handleEnterKeyUp(handleClick)}
          tabIndex="0"
          aria-label={t('pagination.next-page')}
          data-hook="pagination__next"
        >
          {arrowRight}
        </div>
      );
    }
    return null;
  }

  renderLast() {
    const { arrowClass, arrowRight, t } = this.props;
    const { currentPage, numberCount } = this.state;
    const handleClick = this.handleClick(numberCount, Pagination.LAST);

    if (currentPage < numberCount - 2) {
      return (
        <div
          className={classNames(styles.last, arrowClass)}
          onClick={handleClick}
          onKeyUp={handleEnterKeyUp(handleClick)}
          tabIndex="0"
          aria-label={t('pagination.last-page')}
          data-hook="pagination__last"
        >
          {arrowRight}
          {arrowRight}
        </div>
      );
    }
    return null;
  }

  handleClick(page, buttonType) {
    const { onChange, showPerPage } = this.props;
    return () => {
      this.setState({ currentPage: page });
      const from = (page - 1) * showPerPage;
      const to = page * showPerPage;
      onChange({ page, from, to, buttonType });
    };
  }

  render() {
    const { entityCount, showPerPage, className, contentFontClassName } = this.props;

    if (entityCount <= showPerPage) {
      return null;
    }
    return (
      <div className={classNames(className, contentFontClassName, 'forum-text-color')} aria-live="polite">
        {this.renderFirst()}
        {this.renderPrevious()}
        {this.renderNumbers()}
        {this.renderNext()}
        {this.renderLast()}
      </div>
    );
  }
}

Pagination.FIRST = 'first';
Pagination.LAST = 'last';
Pagination.NEXT = 'next';
Pagination.PREVIOUS = 'previous';
Pagination.PAGE = 'page';

export default _.flowRight(withFontClassName, withTranslate)(Pagination);



// WEBPACK FOOTER //
// ./app/components/pagination/pagination.js